import { RESULT_FAMILY_ARRAY_MAX, RESULT_RANK_MAX } from "./results";
import { TIMING_TIME_MAX_MS, type TimingLaneStatus } from "./timing";
import {
	TIMING_BENCH_LEGS_MAX,
	TIMING_BENCH_SKIP_REASONS,
	type TimingBenchLane,
	type TimingBenchLeg,
	type TimingBenchOptions,
	type TimingBenchResult,
	type TimingBenchSkip,
	type TimingBenchSkipReason,
	type TimingBenchSnapshot,
	benchSnapshotToTiming,
} from "./timing-bench";

/**
 * Seiko PT-8000 adapter — the swimming bench's race table (`GET
 * /api/tsr/swimming/results` on the serial-monitoring bench, or its
 * simulator) → a bench snapshot (timing-bench.ts) → one `TimingPayload` for
 * `POST /meets/:id/timing`.
 *
 *   bench JSON ──pt8000ToBenchSnapshot()──▶ TimingBenchSnapshot ──benchSnapshotToTiming()──▶ TimingPayload ──normalizeTiming()──▶ rows
 *
 * `pt8000ToTiming` is exactly those two steps, so a bench read live and a
 * relayed snapshot applied later give the same payload. The snapshot step only
 * converts units and names (timer lane, legs, lap touches, the finish touch's
 * place, the simulator's status); the payload rules live in
 * `benchSnapshotToTiming`, and what is written in `normalizeTiming`
 * (timing.ts). The rules, end to end:
 *
 * - **A lane is `legs[]`**, the bench's per-timer-lane reading. `lane` is the
 *   bench's `poolLane`; until its lane offset is set that is null and the
 *   lane is SKIPPED (`no_pool_lane`) — guessing the offset would write the
 *   wrong swimmer.
 * - **A suspect lane is skipped whole** (`suspect`, with the bench's
 *   reasons), like the normalizer's doubtful lane: the operator keys it in.
 * - **Distances come from the walls** (`poolLaneTouches`): a touch's `lap`
 *   is 0 at the first turn and goes up at each return to the start wall,
 *   `side` says which wall (kept in the snapshot). The bench cuts every race
 *   into legs at the start wall (a 200 m in a 50 m pool is two legs; a relay's
 *   changeovers are such touches too): a completed leg's `to` is the
 *   start-wall touch, the last leg's `to` the finish once it is final. A
 *   split at or beyond the race distance is dropped (the wall at the distance
 *   IS the finish). Cumulative, integer ms.
 * - **The finish is sent only from a `final` reading.** A lane still
 *   swimming sends its splits, no finish. A race that ends at the turning
 *   wall (a 50 m in a 50 m pool) never closes its leg: the lane's finish
 *   record (`finishes[]`) makes its last leg final, at the record's time.
 * - **`place`** is the bench's finish touch place (`finishes[]`, same timer
 *   lane, same time) — only compared by the server, never stored.
 * - **Status** only from the simulator's `track.lanes[].irm` / `dq` (DNS,
 *   DNF, DSQ; DQ = DSQ). Real hardware sends none, so status is omitted.
 *   A track lane with a status and no reading is still sent (status only).
 * - **No surviving lane → `payload: null`**: the endpoint needs ≥ 1 lane.
 */

/** A time as the bench writes it. */
export interface Pt8000Time {
	text?: string;
	totalMs?: number | null;
}

export interface Pt8000Leg {
	/** Timer lane, 0-based. */
	lane: number;
	/** Pool lane once the bench's lane offset is set, else null. */
	poolLane?: number | null;
	/** 1-based; > 1 only in a relay. */
	leg?: number | null;
	time?: Pt8000Time | null;
	from?: Pt8000Time | null;
	to?: Pt8000Time | null;
	/** The leg is complete — its `to` is the touch at the end of it. */
	final?: boolean | null;
	splits?: { lap?: number | null; side?: string | null; cumulative?: Pt8000Time | null; elapsed?: Pt8000Time | null }[] | null;
	suspect?: boolean | null;
	reasons?: string[] | null;
}

export interface Pt8000Touch {
	lane?: number | null;
	poolLane?: number | null;
	place?: number | null;
	lap?: number | null;
	/** "S" the start wall, "T" the turning wall. */
	side?: string | null;
	type?: string | null;
	time?: Pt8000Time | null;
}

export interface Pt8000TrackLane {
	lane: number;
	poolLane?: number | null;
	dq?: string | boolean | null;
	irm?: string | null;
}

/** The fields of the bench's race table the adapter reads. */
export interface Pt8000Feed {
	meetId?: string | null;
	/** The bench's race clock. */
	clock?: { text?: string; totalMs?: number | null; updating?: boolean | null } | null;
	warnings?: string[] | null;
	laneOffset?: number | null;
	resets?: number | null;
	simulated?: boolean | null;
	legs?: Pt8000Leg[] | null;
	finishes?: Pt8000Touch[] | null;
	sources?: { port?: string | null }[] | null;
	/** Simulator only. */
	track?: { poolLength?: number | null; distance?: number | null; relayLegs?: number | null; phase?: string | null; lanes?: Pt8000TrackLane[] | null } | null;
}

export const PT8000_SYSTEM = "SEIKO_PT8000";

export const PT8000_SKIP_REASONS = TIMING_BENCH_SKIP_REASONS;
export type Pt8000SkipReason = TimingBenchSkipReason;
export type Pt8000Skip = TimingBenchSkip;
export type Pt8000Options = TimingBenchOptions;
export type Pt8000Result = TimingBenchResult;

const ms = (t: Pt8000Time | null | undefined): number | null =>
	t && typeof t.totalMs === "number" && Number.isFinite(t.totalMs) && t.totalMs >= 0 && t.totalMs <= TIMING_TIME_MAX_MS ? Math.round(t.totalMs) : null;

const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** An integer in [min, max], else null. */
const intIn = (v: unknown, min: number, max: number): number | null => {
	if (!num(v)) return null;
	const n = Math.trunc(v);
	return n >= min && n <= max ? n : null;
};

/** The simulator's lane verdict as a timing status; real hardware has none. */
export function pt8000Status(t: Pt8000TrackLane | undefined): TimingLaneStatus | undefined {
	if (!t) return undefined;
	const irm = typeof t.irm === "string" ? t.irm.trim().toUpperCase() : "";
	if (irm === "DNS" || irm === "DNF" || irm === "DSQ") return irm;
	if (irm === "DQ" || (t.dq !== null && t.dq !== undefined && t.dq !== false && t.dq !== "")) return "DSQ";
	return undefined;
}

/**
 * The bench's race table → a snapshot BY TIMER LANE (what the bridge relays
 * to `PUT /timing/benches/:benchId`). Pure; it drops what does not fit the
 * snapshot schema (a time beyond 24 h, a lane beyond 99) rather than throw.
 */
export function pt8000ToBenchSnapshot(feed: Pt8000Feed, capturedAt: string): TimingBenchSnapshot {
	const byLane = new Map<number, Pt8000Leg[]>();
	for (const leg of feed.legs ?? []) {
		const lane = intIn(leg.lane, 0, 99);
		if (lane === null || intIn(leg.leg ?? 1, 1, TIMING_BENCH_LEGS_MAX) === null) continue;
		const list = byLane.get(lane) ?? [];
		list.push(leg);
		byLane.set(lane, list);
	}
	for (const list of byLane.values()) list.sort((a, b) => (a.leg ?? 1) - (b.leg ?? 1));

	const trackByLane = new Map<number, Pt8000TrackLane>();
	for (const t of feed.track?.lanes ?? []) {
		const lane = intIn(t.lane, 0, 99);
		if (lane !== null) trackByLane.set(lane, t);
	}

	const lanes: TimingBenchLane[] = [];
	for (const timerLane of [...new Set([...byLane.keys(), ...trackByLane.keys()])].sort((a, b) => a - b)) {
		const legs = byLane.get(timerLane) ?? [];
		const status = pt8000Status(trackByLane.get(timerLane)) ?? null;
		// A track lane with no reading and no verdict says nothing.
		if (legs.length === 0 && status === null) continue;

		const benchLegs: TimingBenchLeg[] = legs.map((leg) => {
			const legNo = Math.trunc(leg.leg ?? 1);
			return {
				leg: legNo,
				final: leg.final === true,
				to_ms: ms(leg.to) ?? (legNo === 1 ? ms(leg.time) : null),
				splits: (leg.splits ?? [])
					.flatMap((s): TimingBenchLeg["splits"] => {
						const lap = intIn(s.lap, 0, 1000);
						const t = ms(s.cumulative);
						const side = typeof s.side === "string" ? s.side.trim().toUpperCase() : "";
						if (lap === null || t === null) return [];
						return [side === "S" || side === "T" ? { lap, cumulative_ms: t, side } : { lap, cumulative_ms: t }];
					})
					.slice(0, RESULT_FAMILY_ARRAY_MAX),
			};
		});
		const last = benchLegs.at(-1);
		// The bench closes a leg only at the start wall, so a race that ends at the
		// turning wall (a 50 m in a 50 m pool) never has a final leg: its finish
		// record says the lane finished, and when.
		if (last && !last.final) {
			const recorded = (feed.finishes ?? []).find((t) => t.lane === timerLane && (t.type ?? "finish") === "finish" && ms(t.time) !== null);
			if (recorded) {
				last.final = true;
				last.to_ms = ms(recorded.time);
			}
		}
		const finishMs = last?.final ? last.to_ms : null;
		const touch =
			finishMs === null
				? undefined
				: (feed.finishes ?? []).find((t) => t.lane === timerLane && ms(t.time) === finishMs && intIn(t.place, 1, RESULT_RANK_MAX) !== null);
		const suspect = legs.filter((l) => l.suspect);

		lanes.push({
			timer_lane: timerLane,
			pool_lane: intIn(legs[0] ? legs[0].poolLane : trackByLane.get(timerLane)?.poolLane, 0, 99),
			legs: benchLegs,
			place: touch ? intIn(touch.place, 1, RESULT_RANK_MAX) : null,
			suspect: suspect.length > 0,
			reasons: suspect
				.flatMap((l) => l.reasons ?? [])
				.filter((r): r is string => typeof r === "string")
				.map((r) => r.slice(0, 200))
				.slice(0, 10),
			status,
		});
	}

	const port = feed.sources?.[0]?.port?.trim().slice(0, 64);
	const clockMs = ms(feed.clock);
	const pool = feed.track?.poolLength;
	const distance = feed.track?.distance;
	return {
		system: PT8000_SYSTEM,
		...(port ? { device_id: port } : {}),
		simulated: feed.simulated === true,
		captured_at: capturedAt,
		clock: clockMs === null ? null : { ms: clockMs, running: feed.clock?.updating === true },
		resets: intIn(feed.resets, 0, Number.MAX_SAFE_INTEGER) ?? 0,
		warnings: (feed.warnings ?? [])
			.filter((w): w is string => typeof w === "string")
			.map((w) => w.slice(0, 200))
			.slice(0, 20),
		pool_length_m: num(pool) && pool > 0 && pool <= 100 ? pool : null,
		distance_m: num(distance) && distance > 0 && distance <= 100_000 ? distance : null,
		relay_legs: intIn(feed.track?.relayLegs, 1, TIMING_BENCH_LEGS_MAX),
		lanes,
	};
}

/** The bench's race table → one timing payload, and the lanes it could not send. Pure. */
export function pt8000ToTiming(feed: Pt8000Feed, opts: Pt8000Options): Pt8000Result {
	return benchSnapshotToTiming(pt8000ToBenchSnapshot(feed, opts.sentAt), opts);
}
